import { User } from "auth/AuthProvider";
import {
  AGENT_RUN_STATUS,
  AgentStatus,
  AgentTypeValue,
  AUTONOMOUS_AGENT_STATUS,
} from "constants/agents";

// =======================
// TYPES: ACL (Access Control List)
// =======================

export type Permission = string;
export type SubjectType = "GROUP" | "USER" | "ROLE";
export type Access = "ALLOW" | "DENY" | "NOT_SET";

export interface ACLEntryPermission {
  permission: Permission;
  access: Access;
}

export interface ACLEntry {
  subject_id: string;
  subject_name: string;
  permissions: ACLEntryPermission[];
  description: string | null;
}

export interface SubjectEntries {
  subject_type: SubjectType;
  acl_entries: ACLEntry[];
}

export interface AgentACLResponse {
  permissions: Permission[];
  types: SubjectType[];
  entries: SubjectEntries[];
}

export interface ACLPayload {
  subject_type: SubjectType;
  subject_name?: string;
  subject_id: string;
  entries: ACLEntryPermission[];
}

// =======================
// LABELS & OPTIONS: ACL
// =======================

export const permissionLabels: Record<Permission, string> = {
  CHAT_WITH_AGENT: "Chat with Agent",
  UPDATE_AGENT: "Update Agent Info",
};

export const accessLabels: Record<Access, string> = {
  ALLOW: "Allow",
  DENY: "Deny",
  NOT_SET: "Not set",
};

export const accessOptions: { label: string; value: Access }[] = [
  { label: accessLabels.ALLOW, value: "ALLOW" },
  { label: accessLabels.DENY, value: "DENY" },
  { label: accessLabels.NOT_SET, value: "NOT_SET" },
];

// =======================
// TYPES: Workspace, Group, Organization, FileSystem, Agent
// =======================

export type Workspace = {
  id?: string;
  workspace_id?: string;
  workspace_name?: string;
  organization_name?: string;
  display_name?: string;
  is_owner?: boolean;
  is_admin?: boolean;
  is_current?: boolean;
  joined_at?: string;
  name?: string;
  permissions?: Permission[];
  acl?: {
    permissions: Permission[];
    types: SubjectType[];
    entries: any[];
  };
  is_workspace_admin?: boolean;
};

export interface MyWorkspacesResponse {
  workspaces: {
    id: string;
    workspace_id: string;
    workspace_name: string;
    organization_name: string;
    display_name: string;
    is_owner: boolean;
    is_current: boolean;
    joined_at: string;
  }[];
}

export type Role = {
  id: string;
  name: string;
  description: string;
  members: User[];
  is_active: boolean;
  // TODO: temporary for backward compatibility
  user_group_id?: string;
  createdAt: string;
  updatedAt: string;
  total_members: number;
  workspace_id: string;
};

// Keep Group type for backward compatibility if needed
export type Group = Role;

export interface Organization {
  id: string;
  name: string;
  is_owner?: boolean;
}

export interface FileSystemItem {
  created_at: string;
  updated_at: string;
  id: string;
  agent_document_id?: string;
  agent_workflow_id?: string;
  name: string;
  parent_id: null;
  workspace_id: string;
  path?: string;
  document_metadata?: {
    file_size: number;
    file_type: string;
    original_filename: string;
    uploaded_at: string;
    status?: DocumentStatus;
  };
  steps?: string[];
  type: "Document" | "Folder" | "Workflow" | "DeepExtract";
  typeName?: string;
  is_default?: boolean;
  status?: DocumentStatus;
}

export interface DataSet {
  id: string;
  name: string;
  path: string;
  agent_id: string;
  workspace_id: string;
  created_at: string;
  is_deleted: boolean;
}

export enum DocumentStatus {
  PROCESSING = "PROCESSING",
  SUCCEED = "SUCCEED",
  FAILED = "FAILED",
}

type AgentScheduleStatus =
  | AGENT_RUN_STATUS.PENDING
  | AGENT_RUN_STATUS.RUNNING
  | AGENT_RUN_STATUS.CANCELLED
  | AGENT_RUN_STATUS.COMPLETED
  | AGENT_RUN_STATUS.FAILED
  | null;

export interface Agent {
  id: string;
  name: string;
  description: string;
  icon_uri: string | null;
  is_active: boolean;
  is_deleted: boolean;
  is_archived: boolean;
  status: AgentStatus | AUTONOMOUS_AGENT_STATUS;
  agent_type?: AgentTypeValue | string;
  cron?: string | null;
  documents: AgentDocument[];
  tools: AgentTool[];
  workflows: AgentWorkflow[];
  chunk_file_size?: number;
  agent_ids?: string[];
  updated_at: string;
  created_at: string;
  autonomous_path?: string;
  schedule_type: string;
  schedule_status?: AgentScheduleStatus;
}

export interface AvailableAgent {
  id: string;
  name: string;
  description: string;
  status: AgentStatus;
  agent_type: AgentTypeValue | string;
}

export interface AgentWorkflow {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  agent_workflow_id: string;
  workflow: any;
}

export interface AgentDocument {
  id: string;
  document: any;
}

export interface AgentTool {
  id: string;
  tool: any;
}

// =======================
// TYPES: Agent Guidance
// =======================
export interface AgentGuidance {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  agent_id: string;
}

export interface AgentGuidancesParams {
  skip?: number;
  take?: number;
  order_by?: string;
  order_direction?: "asc" | "desc";
  handler?: any;
}

export interface AgentGuidancesResponse {
  items: AgentGuidance[];
  total: number;
}

export interface CreateAgentGuidanceRequest {
  name: string;
  description: string;
}

// =======================
// TYPES: Agent Evaluation
// =======================
export enum EvaluationStatus {
  PROCESSING = "Processing",
  ERROR = "Error",
  DONE = "Done",
}

export interface EvaluationHistoryRecord {
  id: string;
  status: EvaluationStatus;
  results: any | null;
  evaluation_dataset_id: string;
  evaluation_dataset_name: string;
  created_at: string;
}

export interface EvaluationResultItem {
  question: string;
  expected_answer: string;
  agent_answer: string;
  score: number;
}

export interface EvaluationResultInfo {
  accuracy: number;
  results: EvaluationResultItem[];
}

// =======================
// TYPES: Feedback
// =======================

export enum FeedbackStatusEnum {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum FeedbackTypeEnum {
  CONTRIBUTE = "CONTRIBUTE",
  DISLIKE = "DISLIKE",
}

export interface Feedback {
  id: string;
  feedback_message: string;
  feedback_type: FeedbackTypeEnum;
  feedback_status: FeedbackStatusEnum;
  message_id: string;
  created_at: string;
  updated_at: string;
  ai_response: string;
  approved_by: string;
  rejected_by: string;
  created_by: string;
  updated_by: string;
  refined_message: string;
}

export interface FeedbackDetail extends Feedback {}

// =======================
// TYPES: Technical Notes
// =======================

export enum TechnicalNoteStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface TechnicalNote {
  id: string;
  question: string;
  answer: string;
  agent_id: string;
  status: TechnicalNoteStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  approved_by: string | null;
  rejected_by: string | null;
}

export interface TechnicalNoteDetail {
  id: string;
  question: string;
  answer: string;
  agent_id: string;
  status: TechnicalNoteStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  approved_by: string | null;
  rejected_by: string | null;
}

// Add new interfaces for API parameters and responses
export interface TechnicalNotesListParams {
  agent_id: string;
  status?: TechnicalNoteStatus | null;
  skip?: number;
  take?: number;
}
export interface TechnicalNotesListManagementParams {
  agent_id: string | null;
  status?: TechnicalNoteStatus | null;
  skip?: number;
  take?: number;
  handler?: any;
  workspace_id?: string | null;
}
export interface TechnicalNotesListResponse {
  total: number;
  items: TechnicalNote[];
}

// Add this interface for creating technical notes
export interface TechnicalNoteCreateRequest {
  question: string;
  answer: string;
  agent_id: string;
}
// Add this interface for creating technical notes
export interface TechnicalNoteUpdateRequest {
  question: string;
  answer: string;
}
// Add this interface for the API response
export interface FeedbackTypeOptions {
  value: string;
  label: string;
}
export interface FeedbackStatusOptions {
  value: string;
  label: string;
}
export interface FeedbackTypesResponse {
  feedback_types: FeedbackTypeOptions[];
}

// Add this interface for the feedback statuses API response
export interface FeedbackStatusesResponse {
  feedback_statuses: FeedbackStatusOptions[];
}

// =======================
// TYPES: Subscriptions
// =======================

export enum SubscriptionStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface Subscription {
  id: string;
  name: string;
  configurations: Record<string, any>;
  subscription_key: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  subscription_plan_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface SubscriptionsListManagementParams {
  skip?: number;
  take?: number;
  search?: string;
  is_active?: boolean;
}

export interface SubscriptionsListResponse {
  total: number;
  items: Subscription[];
}

export interface SubscriptionCreateRequest {
  // Optional to avoid breaking existing UI; backend may require these
  subscription_plan_id?: string;
  name: string;
  start_date: string;
  end_date: string;
  configurations: Record<string, any>;
  is_auto_onboard?: boolean;
  onboard_details?: {
    organization_name: string;
    workspace_name: string;
    admin_name: string;
    admin_email: string;
    admin_password: string;
    llm_model?: {
      name: string;
      type: string;
      model_metadata: Record<string, any>;
    };
    embedding_model?: {
      name: string;
      type: string;
      model_metadata: Record<string, any>;
    };
  };
}

export interface SubscriptionUpdateRequest {
  name: string;
  start_date: string;
  end_date: string;
  configurations: Record<string, any>;
  subscription_plan_id?: string;
  is_auto_onboard?: boolean;
  onboard_details?: {
    organization_name: string;
    workspace_name: string;
    admin_name: string;
    admin_email: string;
    admin_password: string;
    llm_model?: {
      name: string;
      type: string;
      model_metadata: Record<string, any>;
    };
    embedding_model?: {
      name: string;
      type: string;
      model_metadata: Record<string, any>;
    };
  };
}

export enum SubscriptionFrequency {
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
  WEEKLY = "WEEKLY",
}

export interface SubscriptionPlanCreateRequest {
  name: string;
  price?: number;
  frequency?: string;
  configurations: Record<string, any>;
  is_enterprise?: boolean;
}

// =======================
// TYPES: Subscription Plans
// =======================

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  frequency: string;
  configurations: Record<string, any>;
  is_active: boolean;
  is_enterprise?: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface SubscriptionPlansListManagementParams {
  skip?: number;
  take?: number;
  search?: string;
  is_active?: boolean;
}

export interface SubscriptionPlansListResponse {
  total: number;
  items: SubscriptionPlan[];
}

export interface SubscriptionPlanUpdateRequest {
  name: string;
  price?: number;
  frequency?: string;
  configurations: Record<string, any>;
}

export enum ROLE {
  USER = "user",
  SYSTEM = "system",
  ASSISTANT = "assistant",
}

export enum TYPE_RESPONSE {
  SUGGESTION = "suggestions",
  TEXT = "text",
  THINKING = "thinking",
  METADATA = "metadata",
  TOOL_CALLING = "tool_calling",
  REFERENCE = "reference",
  FINAL_ANSWER = "final_answer",
  RESOURCE = "resource",
  ARTIFACT = "artifact",
}

export enum FILE_TYPE {
  POWERPOINT = "powerpoint",
}

export enum LANGUAGE {
  ENGLISH = "English",
  VIETNAMESE = "Vietnamese",
}

export interface UpdateAgentPipelinePayload {
  pipelines: Array<{
    input: string;
    agent_id: string;
    output: string;
  }>;
}

export interface UpdateAgentPipelineResponse {
  message_code: number;
  pipeline_count: number;
}

export interface AgentPipelineStep {
  step: number;
  input: string;
  agent_id: string;
  agent_name: string;
  output: string;
  created_at: string;
  updated_at: string;
}

export interface AgentPipelineResponse {
  pipelines: AgentPipelineStep[];
}

export interface UpdateAgentCronResponse {
  message_code: number;
  cron: string;
}

// =======================
// CONSTANTS: Subscriptions
// =======================

export const SUBSCRIPTION_STATUS_FILTER_OPTIONS = [
  { label: "Active", value: SubscriptionStatus.ACTIVE },
  { label: "Inactive", value: SubscriptionStatus.INACTIVE },
] as const;

// =======================
// TYPES: Onboarding
// =======================

export interface OnboardingData {
  orgName: string;
  workspaceName: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  subscriptionKey: string;
}

export interface OnboardingResponse {
  success: boolean;
  message: string;
  data?: {
    organizationId: string;
    workspaceId: string;
    userId: string;
    subscriptionId: string;
  };
}
